import React, { useState, useEffect } from "react";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  Box,
  TextField,
  Button,
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  IconButton,
  Avatar,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DynamicUploadModal from "../../../components/UploadModal";
import { useLocation, useNavigate } from "react-router-dom";
import { heIL } from "@mui/x-data-grid";

const AddCategory = () => {
  const [category, setCategory] = useState({
    title: "",
    id: "",
    image: "",
    launcherUrl: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const uid = params.get("uid");

  useEffect(() => {
    if (uid) {
      fetchData(uid);
    }
  }, [location.search]);

  const fetchData = async (uid) => {
    try {
      const docRef = doc(db, "banners", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setCategory(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  useEffect(() => {
    setCategory((prevCategory) => ({
      ...prevCategory,
      id: uuidv4(),
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCategory((prevCategory) => ({
      ...prevCategory,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const uniqueId = category.id;

    const categoryData = {
      ...category,
      image: uploadedImageUrl || category.image,
      launcherUrl: category.launcherUrl.toLowerCase(),
      timestamp: serverTimestamp(),
    };

    try {
      await setDoc(doc(db, "banners", uniqueId), categoryData);
      setOpenSnackbar(true);
      setCategory({
        title: "",
        id: uuidv4(),
        image: "",
        launcherUrl: "",
      });
    } catch (error) {
      console.error("Error adding/updating category: ", error);
      alert("Error adding/updating category");
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleUploadSuccess = (url) => {
    setUploadedImageUrl(url);
    setCategory((prevCategory) => ({
      ...prevCategory,
      image: url,
    }));
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Card>
        <CardContent>
          <Avatar
            style={{
              width: 100,
              height: 100,
              margin: "0 auto",
              marginBottom: 20,
            }}
          >
            {category.image ? (
              <img
                src={category.image}
                alt="Category"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <PhotoCamera style={{ fontSize: 50, color: "#2058C8FF" }} />
            )}
          </Avatar>
          <Typography sx={{ fontSize: 16, color: "#326377FF" }}>
            {uid ? "Update Banner" : "Add New Banner"}
          </Typography>
          <Box sx={{ height: "20px" }}></Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={category.title}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ID"
                  name="id"
                  value={category.id}
                  disabled
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="image"
                  value={uploadedImageUrl || category.image}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <IconButton
                  onClick={() => setOpenModal(true)}
                  color="primary"
                  aria-label="upload image"
                >
                  <PhotoCamera />
                </IconButton>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Launcher URL"
                  name="launcherUrl"
                  value={category.launcherUrl}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  {uid ? "Update Banner" : "Add Banner"}
                </Button>
              </Grid>
            </Grid>
          </form>

          <DynamicUploadModal
            open={openModal}
            onClose={handleModalClose}
            uploadPreset="bookMyDr"
            folderName="Madecare"
            maxSize={5 * 1024 * 1024}
            onUploadSuccess={handleUploadSuccess}
          />
        </CardContent>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {category.id
            ? "Banner updated successfully!"
            : "Banner added successfully!"}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddCategory;
